import { animated, useSpring, useSprings } from '@react-spring/web';
import { CTA } from '@volkswagen-onehub/components-core';
import React, { useEffect, useRef, useState } from 'react';
import { useOfertaCard } from '../../../context/ofertas-card-context';
import { MediaAssets } from '../../../services/ofertas-service/models/ofertas';
import {
	CarImagesContainer,
	CarrouselContainer,
	ControlsContainer,
	IconWrapper,
	ModelCarrouselLayout,
	PauseIcon,
	PlayIcon,
	StyledCarImage,
	StyledCarImagesRow,
} from './ui/model-carrousel-styles';

const CarImagesRow = animated(StyledCarImagesRow);
const CarImage = animated(StyledCarImage);

const turntable = [
	'D6Turntable1',
	'D6Turntable3',
	'D6Turntable5',
	'D6Turntable6',
	'D6Turntable7',
	'D6Turntable9',
	'D6Turntable12',
];

const explore = [
	'D6Explore1Alpha',
	'D6Explore2Alpha',
	'D6Explore3Alpha',
	'D6Explore4Alpha',
];

export const ModelCarrousel = (): React.ReactElement => {
	const { selectedOferta, hasOneCarline } = useOfertaCard();
	const mediaAssets = selectedOferta.configuracion.mediaAssets;

	let carImages = [];

	carImages = turntable
		.map((t) => mediaAssets[t as keyof MediaAssets])
		.filter(
			(image): image is string => typeof image === 'string' && image.length > 0,
		);
	if (carImages.length == 0) {
		carImages = explore
			.map((t) => mediaAssets[t as keyof MediaAssets])
			.filter(
				(image): image is string =>
					typeof image === 'string' && image.length > 0,
			);
	}

	const loopImages = [...carImages, ...carImages, ...carImages];
	const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
	const [imageWidth, setImageWidth] = useState(0);
	const imageRef = useRef<HTMLImageElement | null>(null);
	const [isPaused, setIsPaused] = useState(false);

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		if (!imageRef.current || typeof ResizeObserver === 'undefined') return;

		const observer = new ResizeObserver((entries) => {
			const width = entries[0].contentRect.width;
			setImageWidth(width);
		});

		observer.observe(imageRef.current);
		return () => observer.disconnect();
	}, []);

	const [{ x }, carrousel] = useSpring(() => ({ x: 0 }));

	useEffect(() => {
		if (!imageWidth || carImages.length === 0) return;

		const startIndex = carImages.length;
		const initialOffset =
			-startIndex * imageWidth + (windowWidth / 2 - imageWidth / 2 - 120);
		const targetOffset = initialOffset - imageWidth * carImages.length;
		const distance = imageWidth * carImages.length;
		const duration = distance / 0.04; // velocidad fija (0.04) para todos los carrousels (aunque no tengan el mismo número de CarImages) -> t = d/v

		carrousel.start({
			from: { x: initialOffset },
			to: { x: targetOffset },
			loop: true,
			config: { duration: duration },
		});

		return () => {
			carrousel.stop();
		};
	}, [carrousel, imageWidth, windowWidth, carImages.length]);

	useEffect(() => {
		if (isPaused) {
			carrousel.pause();
		} else {
			carrousel.resume();
		}
	}, [isPaused, carrousel]);

	const menuWidth = 250;
	const center = windowWidth / 2;
	const fadeStart = center - menuWidth - imageWidth / 2;
	const fadeEnd = fadeStart + 150;

	const springs = useSprings(
		loopImages.length,
		loopImages.map((_, i) => ({
			opacity: x.to((val) => {
				const imageCenterX = i * imageWidth + val + imageWidth / 2;
				const distance = center - imageCenterX;
				if (distance < fadeStart) return 1;
				if (distance > fadeEnd) return 0.25;
				const fadeRange = fadeEnd - fadeStart;
				const relative = (distance - fadeStart) / fadeRange;
				return 1 - relative * 0.75;
			}),
		})),
	);

	return (
		<ModelCarrouselLayout
			className="model-carrousel__model-carrousel-layout"
			hasOneCarline={hasOneCarline}
		>
			<CarrouselContainer>
				<CarImagesContainer className="model-carrousel__car-images-container">
					<CarImagesRow
						style={{
							width: `${loopImages.length * imageWidth}px`,
							transform: x.to((val) => `translateX(${val}px)`),
						}}
					>
						{loopImages.map((image, index) => (
							<CarImage
								key={index}
								ref={index === 0 ? imageRef : null}
								src={image}
								alt={`Imagen del coche ${index + 1}`}
								style={{ opacity: springs[index].opacity }}
							/>
						))}
					</CarImagesRow>
				</CarImagesContainer>

				<ControlsContainer
					onClick={() => setIsPaused(!isPaused)}
					className="model-carrousel__controls-container"
				>
					<CTA
						tag="button"
						size="small"
						aria-label={isPaused ? 'Reproducir animación' : 'Detener animación'}
						aria-pressed={isPaused}
						icon={
							isPaused ? (
								<IconWrapper>
									<PlayIcon />
								</IconWrapper>
							) : (
								<IconWrapper>
									<PauseIcon />
								</IconWrapper>
							)
						}
						iconPosition="start"
					>
						<>{isPaused ? 'Reproducir' : 'Detener'}</>
					</CTA>
				</ControlsContainer>
			</CarrouselContainer>
		</ModelCarrouselLayout>
	);
};
